import { configureRequestParams, getErrorMessage } from '@/utils/helpers'

import axios from './instance'

/**
 * Resolve the base path for a Course (module) request.
 *
 * - With organizationId → /organizations/<organizationId>/courses/ (the org's
 *   own shared course modules — RSCX-320)
 * - Otherwise → /entities/<entityId>/courses/ (facility-owned + RescueX +
 *   org-shared modules)
 */
const basePath = (entityId, organizationId) =>
  organizationId ? `/organizations/${organizationId}/courses/` : `/entities/${entityId}/courses/`

/**
 * List the courses scoped to an entity (facility-owned + RescueX + org-shared)
 * or to an organization (its own shared modules).
 *
 * @param {object} payload
 * @param {string} [payload.entityId]
 * @param {string} [payload.organizationId]
 * @param {Array} [payload.sorting]
 * @param {object} [payload.pagination]
 * @param {string} [payload.searchTerm]
 * @param {object} [payload.filters]
 * @returns {Promise<object>}
 */
export const getEntityCourses = async (payload) => {
  const { entityId, organizationId, sorting, pagination, searchTerm, filters } = payload
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(`${basePath(entityId, organizationId)}${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Fetch a single entity- or org-scoped course.
 *
 * @param {object} payload
 * @param {string} [payload.entityId]
 * @param {string} [payload.organizationId]
 * @param {string} payload.courseId
 * @returns {Promise<object>}
 */
export const getEntityCourse = async ({ entityId, organizationId, courseId }) => {
  try {
    return await axios.get(`${basePath(entityId, organizationId)}${courseId}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Create a new facility or org course. The backend forces is_xverse_course=false
 * and binds entity / organization from the URL.
 *
 * @param {object} payload
 * @param {string} [payload.entityId]
 * @param {string} [payload.organizationId]
 * @returns {Promise<object>}
 */
export const addEntityCourse = async ({ entityId, organizationId, ...data }) => {
  try {
    return await axios.post(basePath(entityId, organizationId), data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update a facility or org course.
 *
 * @param {object} payload
 * @param {string} [payload.entityId]
 * @param {string} [payload.organizationId]
 * @param {string} payload.id
 * @returns {Promise<object>}
 */
export const updateEntityCourse = async ({ entityId, organizationId, id, ...data }) => {
  try {
    return await axios.patch(`${basePath(entityId, organizationId)}${id}/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Delete a facility or org course.
 *
 * @param {object} payload
 * @param {string} [payload.entityId]
 * @param {string} [payload.organizationId]
 * @param {string} payload.id
 * @returns {Promise<null>}
 */
export const deleteEntityCourse = async ({ entityId, organizationId, id }) => {
  try {
    return await axios.delete(`${basePath(entityId, organizationId)}${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
